import React from "react";
import { getAirportSuggestions } from "../airports/getAirportSuggestions";
import { NEWS_NAMESPACE } from "../search/constants";
import { AutosuggestCriterion } from "../search/criteria/components/autosuggest/AutosuggestCriterion";
import {
	STANDARD_CHOICE_CHECKBOX_CRITERION_INITIAL_STATE,
	StandardChoiceCheckboxCriterion,
} from "../search/criteria/components/checkboxes/StandardChoiceCheckboxCriterion";
import { RadioButtonCriterion } from "../search/criteria/components/radio/RadioButtonCriterion";
import { SearchesCriterion } from "../search/criteria/components/SearchesCriterion";
import { FreeTextCriterion } from "../search/criteria/components/text/FreeTextCriterion";
import { VisibilitySelector } from "../common/VisibilitySelector";
import { SearchType } from "../search/searchCriteriaMapper";
import { airportAutoSuggest, regionsLink } from "../search/searchOptions";
import {
	getAircraftChoicesEndpoint,
	getChoicesEndpoint,
	getCompaniesAutoSuggestEndpoint,
} from "../search/utils";
import { newsSearchBy, newsTypeChoices } from "./searchOptions";
import { CheckboxCriterion } from "../search/criteria/components/checkboxes/CheckboxCriterion";

export const mapSearchCriterion = (key, searchType) => {
	const quickSearch = searchType === SearchType.QUICK_SEARCH;

	switch (key) {
		case "news_search_by":
			return (
				<RadioButtonCriterion
					criterionKey={key}
					label="Search By"
					choices={newsSearchBy}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "news_type":
			return (
				<CheckboxCriterion
					criterionKey={key}
					label="News Category"
					choices={newsTypeChoices}
					closeOnClickOutside={false}
					saveToClientState={true}
					quickSearch={quickSearch}
					isInitiallyOpened={true}
					criterionToClear={{
						key: "publishing_channels",
						value: STANDARD_CHOICE_CHECKBOX_CRITERION_INITIAL_STATE,
					}}
				/>
			);

		case "segments":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="News Segment"
					endpoint={getChoicesEndpoint("aviation_segments")}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "publishing_channels":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Publishing Channel"
					endpoint={getChoicesEndpoint("all_publishing_channels")}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
					isInitiallyOpened={true}
				/>
			);

		case "airline_search":
			return (
				<SearchesCriterion
					criterionKey={key}
					label="Operator Search"
					namespace="airlines"
					visibilitySelector={
						new VisibilitySelector(
							"news_search_by",
							"airlineSearches",
						)
					}
				/>
			);

		case "list":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="List/Favourites"
					endpoint={"/api/v1/airlines/favorites"}
					visibilitySelector={
						new VisibilitySelector(
							"news_search_by",
							"listFavourites",
						)
					}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "carrier":
			return (
				<AutosuggestCriterion
					criterionKey={key}
					label="Company"
					placeholder="Type to search for company"
					endpoint={getCompaniesAutoSuggestEndpoint(NEWS_NAMESPACE)}
					showSelectedChoices
					quickSearch={quickSearch}
				/>
			);

		case "airport":
			return (
				<AutosuggestCriterion
					criterionKey={key}
					label="Airport"
					placeholder="Type to search for airport"
					endpoint={airportAutoSuggest}
					getSuggestions={getAirportSuggestions}
					showSelectedChoices
					quickSearch={quickSearch}
				/>
			);

		case "phrase":
			return (
				<FreeTextCriterion
					criterionKey={key}
					label="Free Text"
					quickSearch={quickSearch}
				/>
			);

		case "continent":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Continent"
					endpoint={getChoicesEndpoint("continent")}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "region":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Operator Region"
					endpoint="/api/v1/regions"
					link={regionsLink}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "country":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Country"
					endpoint={getChoicesEndpoint("country")}
					parentKeys={["continent"]}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "market_group":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Market Group"
					endpoint={getAircraftChoicesEndpoint(
						"aircraft_market_group",
						NEWS_NAMESPACE,
					)}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "manufacturer":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Manufacturer"
					endpoint={getAircraftChoicesEndpoint(
						"manufacturer",
						NEWS_NAMESPACE,
					)}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "aircraft_family":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Aircraft Family"
					endpoint={getAircraftChoicesEndpoint(
						"aircraft_family",
						NEWS_NAMESPACE,
					)}
					parentKeys={["manufacturer"]}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "aircraft":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Aircraft"
					endpoint={getAircraftChoicesEndpoint(
						"aircraft",
						NEWS_NAMESPACE,
					)}
					parentKeys={["manufacturer", "aircraft_family"]}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "aircraft_iata":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Aircraft IATA Code"
					endpoint={getChoicesEndpoint("all_aircraft_types")}
					parentKeys={["market_group", "manufacturer"]}
					parentKeysMap={{ market_group: "aircraft_market_group" }}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);

		case "news_tags":
			return (
				<StandardChoiceCheckboxCriterion
					criterionKey={key}
					label="Tag"
					endpoint={getChoicesEndpoint("tagsSelect")}
					closeOnClickOutside={false}
					quickSearch={quickSearch}
				/>
			);
	}
};

export const newsSearchCriteria = {
	News: [
		"news_search_by",
		"airline_search",
		"news_type",
		"segments",
		"publishing_channels",
		"list",
		"carrier",
		"airport",
		"phrase",
		"continent",
		"region",
		"country",
		"market_group",
		"manufacturer",
		"aircraft_family",
		"aircraft",
		"aircraft_iata",
		"news_tags",
	],
};
